import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Divider,
} from '@mui/material';
import { Close, RoomService } from '@mui/icons-material';

const serviceTypes = ['Restaurant', 'Room Service', 'Laundry', 'Spa'];
const statusOptions = ['Pending', 'Confirmed', 'In Progress', 'Completed', 'Cancelled'];

export default function ServiceOrderDialog({ open, onClose, order, onSave, mode = 'add' }) {
  const [formData, setFormData] = useState({
    type: 'Room Service',
    guest: '',
    room: '',
    items: '',
    amount: '',
    status: 'Pending',
    time: new Date().toTimeString().slice(0, 5),
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (order && mode === 'edit') {
      setFormData({ ...order });
    } else if (mode === 'add') {
      setFormData({
        type: 'Room Service',
        guest: '',
        room: '',
        items: '',
        amount: '',
        status: 'Pending',
        time: new Date().toTimeString().slice(0, 5),
        date: new Date().toISOString().split('T')[0],
        notes: '',
      });
    }
  }, [order, mode]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.guest.trim()) newErrors.guest = 'Guest name is required';
    if (!formData.room.trim()) newErrors.room = 'Room number is required';
    if (!formData.items.trim()) newErrors.items = 'Items/details are required';
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Valid amount is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const orderData = {
      ...formData,
      amount: parseFloat(formData.amount),
      id: order?.id || `SRV${String(Math.floor(Math.random() * 10000)).padStart(3, '0')}`,
    };

    onSave(orderData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      type: 'Room Service',
      guest: '',
      room: '',
      items: '',
      amount: '',
      status: 'Pending',
      time: new Date().toTimeString().slice(0, 5),
      date: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <RoomService color="primary" />
            <Typography variant="h6">
              {mode === 'add' ? 'New Service Order' : 'Edit Service Order'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Order Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Service Type"
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
            >
              {serviceTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Status"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Guest Name"
              value={formData.guest}
              onChange={(e) => handleChange('guest', e.target.value)}
              error={!!errors.guest}
              helperText={errors.guest}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Room Number"
              value={formData.room}
              onChange={(e) => handleChange('room', e.target.value)}
              error={!!errors.room}
              helperText={errors.room}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Items / Service Details"
              multiline
              rows={3}
              value={formData.items}
              onChange={(e) => handleChange('items', e.target.value)}
              error={!!errors.items}
              helperText={errors.items}
              placeholder="E.g., Breakfast Buffet x2, Orange Juice, Coffee"
              required
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={formData.date}
              onChange={(e) => handleChange('date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Time"
              type="time"
              value={formData.time}
              onChange={(e) => handleChange('time', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              error={!!errors.amount}
              helperText={errors.amount}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
              }}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Additional Notes"
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any special requests or notes..."
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          {mode === 'add' ? 'Create Order' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
